// OEM App 定制 — 「当前进度」推导（企业端 / 管理端共用）
// 输入：一/二阶段资料完成度、UI 设计状态、一/二阶段构建状态、是否已创建 App
// 输出：{ key, text, hint, owner, color, target }
//   owner  : company（待企业处理）/ platform（等待平台）/ none（已结束）
//   color  : antd Tag 色 —— warning=待你处理、processing=等待平台、error=异常、success=完成
//   target : 「去处理」跳转参数（详情页 state）

const mk = (key, text, hint, owner, color, target = null) => ({ key, text, hint, owner, color, target });

/**
 * 定制项目当前进度
 * @param {object} p
 * @param {'pending'|'approved'|'rejected'} p.auditStatus 项目级定制状态（审核通过前为最高优先级）
 * @param {{done:number,total:number}} p.p1 一阶段资料完成度
 * @param {{done:number,total:number}} p.p2 二阶段资料完成度
 * @param {'none'|'pending'|'confirmed'|'rejected'} p.uiStatus UI 设计状态
 * @param {'building'|'done'|'failed'|undefined} p.phase1 一阶段构建状态
 * @param {'building'|'done'|'failed'|undefined} p.phase2 二阶段构建状态
 * @param {boolean} p.appCreated 是否已创建 App
 */
export const customProgress = ({ auditStatus, p1 = {}, p2 = {}, uiStatus = 'none', phase1, phase2, appCreated = false }) => {
    // 定制申请审核（项目级闸门）：审核通过前优先展示，不进入资料 / 构建流程
    if (auditStatus === 'rejected') return mk('audit_rejected', '定制申请已驳回', '请修改申请信息后重新提交', 'company', 'error', { editAudit: true });
    if (auditStatus === 'pending') return mk('audit_pending', '待平台审核', '平台正在审核你的定制申请，请耐心等待', 'platform', 'processing');

    const p1Done = (p1.total || 0) > 0 && p1.done >= p1.total;
    const p2Done = (p2.total || 0) > 0 && p2.done >= p2.total;
    const p1Label = (p1.total || 0) > 0 ? `（${p1.done}/${p1.total}）` : '';
    const p2Label = (p2.total || 0) > 0 ? `（${p2.done}/${p2.total}）` : '';

    if (phase2 === 'done') return mk('done', '定制完成，App 已交付', '可在「我的App」查看与下载', 'none', 'success');
    if (phase2 === 'building') return mk('p2_building', '二阶段构建中', '平台正在构建，请耐心等待', 'platform', 'processing');
    if (phase2 === 'failed') return mk('p2_failed', '二阶段构建失败', '平台处理中，如有疑问请联系客服', 'platform', 'error');

    if (phase1 === 'done' && !appCreated) return mk('wait_create_app', '待平台创建应用', '一阶段已构建完成，平台将为你创建 App', 'platform', 'processing');
    if (phase1 === 'done' && appCreated && !p2Done) return mk('fill_p2', `待你完善二阶段资料${p2Label}`, '请填写开发二阶段所需资料', 'company', 'warning', { stage: 'phase2' });
    if (phase1 === 'done' && appCreated && p2Done) return mk('wait_build_p2', '待平台构建二阶段', '资料已齐全，等待平台构建', 'platform', 'processing');

    if (phase1 === 'building') return mk('p1_building', '一阶段构建中', '平台正在构建，请耐心等待', 'platform', 'processing');
    if (phase1 === 'failed') return mk('p1_failed', '一阶段构建失败', '平台处理中，如有疑问请联系客服', 'platform', 'error');

    if (uiStatus === 'confirmed' && p1Done) return mk('wait_build_p1', '待平台构建一阶段', '资料与 UI 已就绪，等待平台构建', 'platform', 'processing');
    if (uiStatus === 'rejected') return mk('ui_rejected', 'UI 设计已驳回', '平台按你的意见修改后会重新提交，请留意', 'platform', 'error');
    if (uiStatus === 'pending') return mk('ui_pending', '待你确认 UI 设计', '请确认平台上传的设计稿', 'company', 'warning', { tab: 'ui' });
    if (!p1Done) return mk('fill_p1', `待你完善一阶段资料${p1Label}`, '请填写开发一阶段所需资料', 'company', 'warning', { stage: 'phase1' });

    return mk('wait_upload_ui', '待平台上传 UI 设计', '资料已齐全，等待平台上传设计稿', 'platform', 'processing');
};
